import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getStaticContent } from "../i18n/staticPages";

const SupportPage = () => {
  const sc = getStaticContent();
  const c = sc.support;

  const icons = [
    <svg key="i0" className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>,
    <svg key="i1" className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>,
    <svg key="i2" className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>,
    <svg key="i3" className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>,
  ];
  const meta = [
    { link: "mailto:info@nktteklif.com", color: "amber" },
    { link: "tel:+905323468920", color: "teal" },
    { link: "/faq", color: "purple", internal: true },
    { link: "#", color: "red", disabled: true },
  ];
  const supportOptions = c.options.map((o, i) => ({ ...o, icon: icons[i], ...meta[i] }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/nkt-bee-logo.png" alt="NKT Logo" className="w-8 h-8 object-contain" />
            <span className="text-xl font-bold text-white">NKT - Teklif Hazırla</span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10">
              {sc.common.home}
            </Button>
          </Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-white mb-4">{c.title}</h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">{c.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {supportOptions.map((option, index) => (
              <Card
                key={index}
                className={`bg-slate-800/50 border-slate-700 hover:border-${option.color}-500/50 transition group ${option.disabled ? 'opacity-60' : ''}`}
              >
                <CardContent className="pt-6 text-center">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-${option.color}-500/20 flex items-center justify-center text-${option.color}-400 group-hover:bg-${option.color}-500/30 transition`}>
                    {option.icon}
                  </div>
                  <h3 className="text-white font-semibold mb-2">{option.title}</h3>
                  <p className="text-slate-400 text-sm mb-4">{option.description}</p>
                  {option.disabled ? (
                    <span className="text-slate-500 text-sm">{option.action}</span>
                  ) : option.internal ? (
                    <Link to={option.link}>
                      <Button variant="outline" size="sm" className={`border-${option.color}-500 text-${option.color}-400 hover:bg-${option.color}-500/10`}>
                        {option.action}
                      </Button>
                    </Link>
                  ) : (
                    <a href={option.link}>
                      <Button variant="outline" size="sm" className={`border-${option.color}-500 text-${option.color}-400 hover:bg-${option.color}-500/10`}>
                        {option.action}
                      </Button>
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">{c.guidesTitle}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {c.guides.map((guide, index) => (
                <Card key={index} className="bg-slate-800/50 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-amber-400">{guide.title}</CardTitle>
                    <CardDescription>{guide.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-2">
                      {guide.steps.map((step, stepIndex) => (
                        <li key={stepIndex} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-sm flex items-center justify-center font-medium">
                            {stepIndex + 1}
                          </span>
                          <span className="text-slate-300 text-sm">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <Card className="bg-slate-800/50 border-slate-700 mb-12">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
                {c.statusTitle}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                {[c.webApp, c.pdfService, c.database].map((label, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    <span className="text-slate-300 text-sm">{label}</span>
                    <span className="text-green-400 text-xs ml-auto">{c.running}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-amber-500/30">
            <CardContent className="py-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">{c.ctaTitle}</h3>
              <p className="text-slate-300 mb-6">{c.ctaDesc}</p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Link to="/contact">
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {sc.common.contactBtn}
                  </Button>
                </Link>
                <a href="tel:+905323468920">
                  <Button variant="outline" className="border-white text-white hover:bg-white/10">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    {c.callNow}
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
